import { MoodLighting } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

export function MoodLightingDemo() {
  return (
    <Section
      id="mood-lighting"
      title="MoodLighting"
      description="Ambient gradient field + optional cursor glow behind reserved-moment surfaces (hero / empty / onboarding / preview-stage). Full-bleed backdrop — distinct from the BorderGlow / EditingBeam / BorderBeam border effects. Token-driven (no raw hex), SSR-safe, reduced-motion aware. Drop it as the first child of a relative host; content sits as a higher-z sibling."
    >
      <VariantBlock
        label="Light field behind a card"
        caption='tone="light" — drifts slowly, follows the app surface'
      >
        <div className="relative h-56 w-80 overflow-hidden rounded-2xl border border-border bg-background">
          <MoodLighting tone="light" />
          <div className="relative z-10 p-6">
            <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              Good morning
            </p>
            <h3 className="mt-2 text-lg font-semibold text-foreground">
              What do you want to make today?
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              The field drifts behind this card. Reading attention stays on the content.
            </p>
          </div>
        </div>
      </VariantBlock>

      <VariantBlock
        label="Dark stage mat"
        caption='tone="stage" — fixed dark ink mat + high-alpha corner glow, independent of app dark mode'
      >
        <div className="relative h-56 w-80 overflow-hidden rounded-2xl border border-border">
          <MoodLighting tone="stage" />
          <div className="relative z-10 p-6">
            <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-white/60">
              Preview stage
            </p>
            <h3 className="mt-2 text-lg font-semibold text-white">
              A dark stage for the spotlight moment.
            </h3>
            <p className="mt-2 text-sm text-white/70">
              The mat stays dark in both light and dark themes — a local backdrop, not a theme
              switch.
            </p>
          </div>
        </div>
      </VariantBlock>

      <VariantBlock
        label="Interactive cursor glow"
        caption="interactive intensity='default' — a soft accent glow fades in and follows the pointer within the box"
      >
        <div
          id="mood-interactive"
          className="relative grid h-72 w-full max-w-md place-items-center overflow-hidden rounded-2xl border border-border bg-background"
        >
          <MoodLighting tone="light" interactive intensity="default" glowRadius={420} />
          <div className="relative z-10 flex flex-col items-center gap-2 text-center">
            <span className="rounded-full border border-border bg-background/70 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground backdrop-blur">
              ↪ Move your cursor here
            </span>
            <p className="max-w-xs text-sm text-muted-foreground">
              A glow wakes on enter and tracks your pointer across the box.
            </p>
          </div>
        </div>
      </VariantBlock>

      <VariantBlock
        label="Hover response"
        caption="hoverResponse — hovering lifts the card (consumer's own hover utilities) AND brightens the field underneath"
      >
        {/* The card-lift lives on the consumer host (the backdrop is
            pointer-events-none); MoodLighting brightens its own field via the
            hoverResponse prop. Both fire on the same hover. */}
        <div className="group relative h-56 w-80 cursor-pointer overflow-hidden rounded-2xl border border-border bg-background transition-all duration-320 ease-smooth-out hover:-translate-y-0.5 hover:shadow-glow-lg">
          <MoodLighting tone="light" hoverResponse intensity="default" />
          <div className="relative z-10 p-6">
            <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              Good morning
            </p>
            <h3 className="mt-2 text-lg font-semibold text-foreground">
              What do you want to make today?
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Hover this card — it lifts and the field behind it wakes.
            </p>
          </div>
        </div>
      </VariantBlock>
    </Section>
  );
}
